import { api } from "./client";
import type { ApiResponse } from "./types";

// Unified card shape the Dharohar page renders. The backend returns each
// entity under different keys with different field names (personalities use
// `name`/`shortBio`, art-forms/handicrafts use `name`/`shortDesc`, none carry a
// `type` or `state`), so every fetch below normalizes into this one shape.
export interface DharoharCard {
  type: "personality" | "art_form" | "handicraft";
  id: string;
  title: string;
  description: string | null;
  imageUrl?: string | null;
  state?: string | null;
  era?: string | null;
  /** Daily card only: "roots" or "all_india" — where today's pick came from. */
  scope?: string | null;
}

export interface DailyDharohar {
  date: string;
  personality: DharoharCard | null;
  artForm: DharoharCard | null;
  handicraft: DharoharCard | null;
}

type RawCard = Record<string, unknown>;

function toCard(type: DharoharCard["type"], raw: RawCard): DharoharCard {
  return {
    type,
    id: String(raw.id ?? ""),
    title: String(raw.name ?? raw.title ?? ""),
    description: (raw.shortBio ?? raw.shortDesc ?? raw.description ?? null) as
      | string
      | null,
    imageUrl: (raw.imageUrl ?? null) as string | null,
    state: (raw.stateName ?? raw.state ?? null) as string | null,
    era: (raw.era ?? null) as string | null,
    scope: (raw.selectionScope ?? null) as string | null,
  };
}

export async function getDaily(): Promise<ApiResponse<DailyDharohar>> {
  const res = await api.get<ApiResponse<RawCard>>("/dharohar/daily");
  const d = res.data;
  return {
    ...res,
    data: {
      date: String(d.date ?? ""),
      personality: d.personalitySpotlight
        ? toCard("personality", d.personalitySpotlight as RawCard)
        : null,
      artForm: d.artFormOfWeek
        ? toCard("art_form", d.artFormOfWeek as RawCard)
        : null,
      handicraft: d.handicraftOfWeek
        ? toCard("handicraft", d.handicraftOfWeek as RawCard)
        : null,
    },
  };
}

type DharoharList = { items: DharoharCard[]; meta: { total: number } };

async function fetchDharoharList(
  path: string,
  key: string,
  type: DharoharCard["type"],
  params?: { state?: string; page?: number; limit?: number },
): Promise<ApiResponse<DharoharList>> {
  const res = await api.get<ApiResponse<Record<string, unknown>>>(
    path,
    params as Record<string, string | number | boolean | undefined>,
  );
  const rows = (res.data[key] as RawCard[] | undefined) ?? [];
  const meta = (res.data.meta as { total: number } | undefined) ?? { total: 0 };
  return {
    ...res,
    data: { items: rows.map((r) => toCard(type, r)), meta },
  };
}

export function listPersonalities(params?: { state?: string; page?: number; limit?: number }) {
  return fetchDharoharList("/dharohar/personalities", "personalities", "personality", params);
}

export function listArtForms(params?: { state?: string; page?: number; limit?: number }) {
  return fetchDharoharList("/dharohar/art-forms", "artForms", "art_form", params);
}

export function listHandicrafts(params?: { state?: string; page?: number; limit?: number }) {
  return fetchDharoharList("/dharohar/handicrafts", "handicrafts", "handicraft", params);
}
